/**
 * De uitgaande vertaalknop in het chatvenster (06-10-2026, Jordy).
 *
 * 18-10-2026 (Jordy, DEFINITIEF): standaard AAN in elke chat, F2F en FanCentro
 * gelijk. "Aan" = automatisch: de SERVER vertaalt naar het Nederlands alleen als
 * de fan zelf Nederlands schrijft (src/services/vertaal_standaard.py), anders
 * gaat het zoals getypt. Daarom stuurt "aan" GEEN vaste doeltaal mee
 * (`doel` = null). "Uit" = altijd zoals getypt. Een handmatige klik geldt
 * voor het geopende gesprek; bij wisselen of verversen geldt weer de standaard.
 */

export interface VertaalStandaard {
  on: boolean
  reason: string
  target?: string | null
  /** 15-10-2026 (Jordy): F2F — altijd aan, de chatter kan hem niet uitzetten. */
  locked?: boolean
}

export interface KnopStand {
  aan: boolean
  label: string
  titel: string
  /** De doeltaal die naar de server gaat als de knop aan staat. */
  doel: string | null
  /** 15-10-2026: vast (F2F). Grijs, niet te klikken; de server beslist de taal
   *  en het venster stuurt geen vertaalvelden mee. */
  vast: boolean
}

const REDEN_UIT: Record<string, string> = {
  fan_writes_other: 'Off — fan does not write Dutch',
  // 18-10-2026 (Jordy): op FanCentro alleen als je hem zelf aanzet.
  // Oud (tot 18-10-2026): FC stond standaard uit.
  fc_off_by_default: 'Off — FanCentro: on only when you turn it on',
  language_unclear: 'Off — fan language not clear yet',
}

/** Standaard als de server (nog) niets meestuurt. 14-10-2026 (Jordy): F2F en FC gelijk,
 *  alleen Nederlands als de fan Nederlands spreekt; dat beslist de server. */
export function terugval(_platform: string | null | undefined): VertaalStandaard {
  // 18-10-2026 (Jordy): standaard aan; zonder bekende fan-taal gaat het zoals getypt.
  return { on: true, reason: 'language_unclear', target: null }
}

/**
 * 20-10-2026 (Jordy): de chatter kiest de stand zelf uit een klein menu, ook
 * terug naar Nederlands vanuit "uit". 'auto' = de standaard van de server (F2F
 * Nederlands tenzij de fan Engels schrijft, FC Nederlands bij een Nederlandse
 * fan); 'nl' = altijd naar het Nederlands (vaste doeltaal); 'uit' = zoals getypt.
 * De keuze geldt voor het geopende gesprek. `true`/`false` (oude knop) blijven
 * werken: true = auto, false = uit.
 */
export type Keuze = 'auto' | 'nl' | 'uit'

export function alsKeuze(handmatig: boolean | Keuze | null): Keuze | null {
  if (handmatig === false) return 'uit'
  if (handmatig === true) return 'auto'
  return handmatig
}

/** De standen in het menu, met de uitleg die de chatter ziet. */
export function keuzeMenu(
  standaard: VertaalStandaard,
  platform?: string | null,
): { keuze: Keuze; label: string; uitleg: string }[] {
  const auto = knopStand(standaard, null)
  // De standaard verschilt per platform (src/services/vertaal_standaard.py).
  const autoUitleg = String(platform || '').toLowerCase() === 'f2f'
    ? 'Default for F2F: Dutch unless the fan writes English'
    : 'Default for FanCentro: Dutch only if the fan writes Dutch'
  return [
    { keuze: 'nl', label: '→ Dutch', uitleg: 'Always send in Dutch (your English is translated)' },
    { keuze: 'auto', label: `Auto · ${auto.label}`, uitleg: autoUitleg },
    { keuze: 'uit', label: 'Translations off', uitleg: 'Send exactly as typed' },
  ]
}

export function knopStand(
  standaard: VertaalStandaard,
  handmatigIn: boolean | Keuze | null,
): KnopStand {
  const keuze = alsKeuze(handmatigIn)
  if (keuze === 'nl' && !standaard.locked) {
    return {
      aan: true,
      vast: false,
      label: '→ Dutch',
      titel: 'Dutch — chosen by you for this chat: your English is sent in Dutch',
      doel: 'nl',
    }
  }
  // 'auto' = de standaard ("geen keuze"); 'uit' = de oude handmatige uit. Een
  // boolean (oude knop) blijft zoals hij was.
  const handmatig: boolean | null = typeof handmatigIn === 'boolean'
    ? handmatigIn
    : keuze === 'uit' ? false : null
  if (standaard.locked) {
    const nl = standaard.target === 'nl'
    return {
      aan: true,
      vast: true,
      label: nl ? 'Always on → Dutch' : 'Always on → English',
      titel: 'Always on for F2F — ' + (nl
        ? 'fan writes Dutch: your English is sent in Dutch'
        : 'fan does not write Dutch (yet): sent in English as typed'),
      doel: standaard.target ?? null,
    }
  }
  const aan = handmatig ?? standaard.on
  // 18-10-2026 (Jordy): "aan" vertaalt alleen voor een fan die Nederlands schrijft.
  const nl = standaard.target === 'nl'
  let titel: string
  if (!aan) {
    titel = handmatig === false
      ? 'Off — turned off by you for this chat: always sent as typed'
      : (REDEN_UIT[standaard.reason] ?? 'Off') + ': sent as typed'
  } else {
    // 19-10-2026 (Jordy): F2F standaard Nederlands (alleen Engels bij een Engelse
    // fan); FanCentro alleen Nederlands bij een Nederlandse fan.
    const waarom = nl
      ? (standaard.reason === 'f2f_default_dutch'
        ? 'F2F: Dutch unless the fan writes English: your English is sent in Dutch'
        : 'fan writes Dutch: your English is sent in Dutch')
      : (standaard.reason === 'fan_writes_english'
        ? 'fan writes English: sent in English as typed'
        : 'fan does not write Dutch (yet): sent in English as typed')
    titel = (handmatig ? 'On — turned on by you for this chat. ' : 'On — ') + waarom
  }
  return {
    aan,
    label: !aan ? 'Translations off' : nl ? '→ Dutch' : 'On · English',
    titel,
    // Geen vaste doeltaal: de server beslist op de taal van de fan.
    doel: null,
    vast: false,
  }
}

/** Is dit de fout van een mislukte vertaling (de server verstuurde niets)? */
export function isVertaalFout(reden: string | null | undefined): boolean {
  return /vertaling mislukt|translation failed/i.test(String(reden || ''))
}

/** 14-10-2026 (Jordy): de chatter leest Engels. Oude Nederlandse vertaalfouten (nog in
 *  bestaande berichten) worden in het Engels getoond, met de vraag het te melden. */
export const MELD_VIA_TELEGRAM = 'Please report this via Telegram.'

export function foutTekst(reden: string | null | undefined): string {
  const tekst = String(reden || '')
  if (/vertaling mislukt/i.test(tekst)) {
    return `Translation failed: nothing was sent. ${MELD_VIA_TELEGRAM}`
  }
  if (/vertaling niet beschikbaar/i.test(tekst)) return `Translation unavailable right now. ${MELD_VIA_TELEGRAM}`
  return tekst
}
